import { ExpandMore } from '@mui/icons-material';
import { IconButton } from '@mui/material';
import { MouseEvent, ReactNode } from 'react';
import { useDatagridContext, useExpanded, useRecordContext, useResourceContext, useTranslate } from 'react-admin';

export interface ExpandRowToggleProps {
  /** i18n key of the accessible name. It stays fixed; aria-expanded carries the state. */
  labelKey: string;
  /** Read by the Datagrid header and DatagridRow off this element, not by the component. */
  label?: ReactNode;
  sortable?: boolean;
  cellClassName?: string;
  headerClassName?: string;
}

/**
 * The keyboard-reachable expand control of one Datagrid row. react-admin's own ExpandRowButton is
 * aria-hidden with tabIndex -1, so ExpandableDatagrid hides it and puts this in the first column.
 *
 * **Only valid inside ExpandableDatagrid**, which is why the folder's index does not export it.
 * The toggle cannot see the grid's `expand`, so it relies on the wrapper, whose `expand` is
 * required; anywhere else it could offer a toggle with no panel, next to react-admin's own cell.
 *
 * `isRowExpandable` and `expandSingle` come from the Datagrid's context, exactly where
 * DatagridRow reads them, and it calls `useExpanded(resource, id, expandSingle)` as DatagridRow
 * does. So both toggle the same store key and the same panel, whose id DatagridRow sets to
 * `${id}-expand`. A record without an id gets nothing: DatagridBody keys that row `row${index}`,
 * which the toggle cannot know.
 */
export const ExpandRowToggle = ({ labelKey }: ExpandRowToggleProps) => {
  const context = useDatagridContext();
  const record = useRecordContext();
  const resource = useResourceContext();
  const translate = useTranslate();
  const [expanded, toggleExpanded] = useExpanded(resource ?? '', record?.id ?? '', context && context.expandSingle);

  if (!record || record.id == null) {
    return null;
  }

  // DatagridRow's own test (ra-ui-materialui DatagridRow.tsx:79-83), minus its `&& expand`, which
  // ExpandableDatagrid guarantees. Boolean(): the activity-log predicates return an object.
  const expandable = !context || !context.isRowExpandable || Boolean(context.isRowExpandable(record));
  if (!expandable) {
    return null;
  }

  // DatagridRow puts its own onClick (rowClick) on the <tr> and never stops propagation.
  const handleClick = (event: MouseEvent) => {
    event.stopPropagation();
    toggleExpanded();
  };

  return (
    <IconButton
      size="small"
      aria-label={translate(labelKey)}
      aria-expanded={expanded}
      aria-controls={`${record.id}-expand`}
      onClick={handleClick}
    >
      <ExpandMore
        fontSize="small"
        sx={(theme) => ({
          transform: expanded ? 'rotate(180deg)' : 'none',
          transition: theme.transitions.create('transform', { duration: theme.transitions.duration.shortest }),
        })}
      />
    </IconButton>
  );
};
